import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Tabs } from "@package/subframe/components/Tabs";
import { Tooltip } from "@package/subframe/components/Tooltip";
import * as SubframeCore from "@subframe/core";
import {
  FeatherAlertCircle,
  FeatherCheckSquare,
  FeatherHistory,
  FeatherMoreVertical,
  FeatherPenTool,
  FeatherPlus,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { TaskManagerFilters } from "@/components/task-manager/task-manager-filters";
import { TaskManagerTable } from "@/components/task-manager/task-manager-table";
import { CountBadge } from "@/components/ui/count-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TableScrollContainer } from "@/components/ui/table-scroll-container";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import { useUpdateActionStatusCommandControllerUpdateStatus } from "@/generated/follow-up-actions/follow-up-actions";
import { useListNurseryMembersQueryControllerListMembers } from "@/generated/members/members";
import type { FollowUpActionResponseDtoDueStatus } from "@/generated/model/followUpActionResponseDtoDueStatus";
import { UpdateActionStatusDtoStatus } from "@/generated/model/updateActionStatusDtoStatus";
import { useTaskManagerData } from "@/hooks/use-task-manager-data";
import type { SortDir } from "@/lib/category-display";
import { invalidateFollowUpActionQueries } from "@/lib/follow-up-actions/invalidate-follow-up-action-queries";
import { canManageTasks } from "@/lib/roles";
import {
  decodeAssigneeFilter,
  DEFAULT_SORT,
  DEFAULT_TAB,
  EMPTY_FILTERS,
  hasActiveFilters,
  itemNounFor,
  PAGE_SIZE,
  supportsDueStatusFilter,
  type TaskManagerSortColumn,
  type TaskManagerTab,
} from "@/lib/task-manager";

const routeApi = getRouteApi("/_authenticated/task-manager/");

/** Copy shown when a tab has nothing to list. */
const EMPTY_MESSAGE: Record<TaskManagerTab, string> = {
  "follow-up-actions": "No open follow-up actions to show for this view.",
  tasks: "General tasks are coming soon. There is nothing to show here yet.",
  "awaiting-acknowledgement": "Nothing is awaiting acknowledgement.",
  history: "No completed or cancelled tasks to show for this view.",
};

export function TaskManagerPage() {
  const navigate = useNavigate();
  const search = routeApi.useSearch();
  const {
    selectedNurseryId,
    selectedNursery,
    nurseries,
    nurseryRole,
    setSelectedNursery,
  } = useNursery();
  const { isPlatformAdmin } = useAuth();
  const { isOrgAdmin } = useOrganisation();
  const queryClient = useQueryClient();

  const tab = (search.tab as TaskManagerTab) ?? DEFAULT_TAB;

  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [assignee, setAssignee] = useState<string | undefined>(undefined);
  const [dueStatus, setDueStatus] = useState<
    FollowUpActionResponseDtoDueStatus | undefined
  >(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sortColumn, setSortColumn] = useState<
    TaskManagerSortColumn | undefined
  >(undefined);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const canAddTask = canManageTasks({
    isPlatformAdmin,
    isOrgAdmin,
    nurseryRole,
  });

  const { data: categoriesResponse } = useListCategoriesQueryControllerList();
  const categories = categoriesResponse?.data.items ?? [];

  const { data: membersResponse } =
    useListNurseryMembersQueryControllerListMembers(
      { nurseryId: selectedNurseryId ?? "", limit: 100 },
      { query: { enabled: !!selectedNurseryId } },
    );
  const members = membersResponse?.data?.data ?? [];

  const updateStatus = useUpdateActionStatusCommandControllerUpdateStatus();

  // Debounce the search box so each keystroke doesn't hit the API.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // The status filter only applies where rows have a due bucket; drop it when
  // switching to a tab that can't honour it, so it doesn't silently persist.
  const showDueStatus = supportsDueStatusFilter(tab);
  useEffect(() => {
    if (!showDueStatus && dueStatus !== undefined) {
      setDueStatus(undefined);
      setPage(0);
    }
  }, [showDueStatus, dueStatus]);

  const effectiveSort = sortColumn
    ? { column: sortColumn, dir: sortDir }
    : DEFAULT_SORT[tab];

  const { items, total, isLoading, isError, counts } = useTaskManagerData({
    nurseryId: selectedNurseryId ?? undefined,
    tab,
    categoryId,
    assignee: decodeAssigneeFilter(assignee),
    dueStatus: showDueStatus ? dueStatus : undefined,
    search: searchTerm,
    sortBy: effectiveSort.column,
    sortDir: effectiveSort.dir,
    page,
    pageSize,
  });

  const setTab = (next: TaskManagerTab) => {
    setPage(0);
    setSortColumn(undefined);
    navigate({ to: "/task-manager", search: { tab: next } });
  };

  const handleSort = (column: TaskManagerSortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDir("asc");
    }
    setPage(0);
  };

  const resetFilters = () => {
    setCategoryId(EMPTY_FILTERS.categoryId);
    setAssignee(EMPTY_FILTERS.assignee);
    setDueStatus(EMPTY_FILTERS.dueStatus);
    setSearchInput(EMPTY_FILTERS.search);
    setSearchTerm(EMPTY_FILTERS.search);
    setPage(0);
  };

  const invalidateActions = () => invalidateFollowUpActionQueries(queryClient);

  const changeStatus = async (
    actionId: string,
    status: UpdateActionStatusDtoStatus,
    successMessage: string,
  ) => {
    try {
      await updateStatus.mutateAsync({ actionId, data: { status } });
      toast.success(successMessage);
      await invalidateActions();
    } catch {
      toast.error("Failed to update the task");
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-metrics font-metrics text-primary-navy-800">
            Task Manager
          </span>
          <span className="text-body font-body text-subtext-color">
            Follow-up actions and tasks for your nursery, in one place.
          </span>
        </div>
        {canAddTask && (
          // Disabled until general task creation exists. The role gate stays
          // live so the permission behaviour is testable now, and enabling it
          // later is just dropping `disabled` and the tooltip wrapper.
          <SubframeCore.Tooltip.Provider>
            <SubframeCore.Tooltip.Root>
              <SubframeCore.Tooltip.Trigger asChild={true}>
                <span className="flex-none">
                  <Button icon={<FeatherPlus />} disabled={true}>
                    Add Task
                  </Button>
                </span>
              </SubframeCore.Tooltip.Trigger>
              <SubframeCore.Tooltip.Portal>
                {/*
                 * No `asChild` here: several duplicate copies of
                 * @radix-ui/react-slot are installed, and its Slottable marker
                 * is a module-local Symbol. Radix's tooltip builds Slottable
                 * from one copy while the popper checks it against another, so
                 * the check fails, Slot sees two children (the content plus
                 * Radix's visually-hidden label) and throws
                 * `React.Children.only`. Rendering Tooltip as a plain child
                 * keeps Radix's own wrapper element and avoids Slot entirely.
                 */}
                <SubframeCore.Tooltip.Content
                  side="bottom"
                  align="center"
                  sideOffset={4}
                >
                  <Tooltip>Task creation is coming soon</Tooltip>
                </SubframeCore.Tooltip.Content>
              </SubframeCore.Tooltip.Portal>
            </SubframeCore.Tooltip.Root>
          </SubframeCore.Tooltip.Provider>
        )}
      </div>

      {/* Tabs */}
      <div className="w-full" data-testid="task-manager-tabs">
        <Tabs>
          <Tabs.Item
            active={tab === "follow-up-actions"}
            icon={<FeatherAlertCircle />}
            onClick={() => setTab("follow-up-actions")}
          >
            Open Follow Up Actions
            <CountBadge count={counts.followUpActions} />
          </Tabs.Item>
          <Tabs.Item
            active={tab === "tasks"}
            icon={<FeatherCheckSquare />}
            onClick={() => setTab("tasks")}
          >
            Open Tasks
            <CountBadge count={counts.tasks} />
          </Tabs.Item>
          <Tabs.Item
            active={tab === "awaiting-acknowledgement"}
            icon={<FeatherPenTool />}
            onClick={() => setTab("awaiting-acknowledgement")}
          >
            Awaiting Acknowledgement
            <CountBadge count={counts.awaitingAcknowledgement} />
          </Tabs.Item>
          <Tabs.Item
            active={tab === "history"}
            icon={<FeatherHistory />}
            onClick={() => setTab("history")}
          >
            History
            <CountBadge count={counts.history} />
          </Tabs.Item>
        </Tabs>
      </div>

      <div className="flex w-full flex-col items-start gap-4 pb-8">
        <TaskManagerFilters
          categories={categories}
          categoryId={categoryId}
          onCategoryChange={(next) => {
            setCategoryId(next);
            setPage(0);
          }}
          nurseries={nurseries}
          selectedNurseryId={selectedNurseryId}
          onNurseryChange={(nurseryId) => {
            const nursery = nurseries.find((n) => n.id === nurseryId);
            if (nursery) {
              setSelectedNursery(nursery.id, nursery.organisationId);
              // The assignee list is per-nursery, so a held user id belongs to
              // the nursery being left. Keeping it would go on filtering while
              // the control fell back to its placeholder — an empty list with no
              // visible cause. Categories are global, so they survive.
              setAssignee(undefined);
              setPage(0);
            }
          }}
          members={members}
          assignee={assignee}
          onAssigneeChange={(next) => {
            setAssignee(next);
            setPage(0);
          }}
          showDueStatus={showDueStatus}
          dueStatus={dueStatus}
          onDueStatusChange={(next) => {
            setDueStatus(next);
            setPage(0);
          }}
          search={searchInput}
          onSearchChange={setSearchInput}
          canReset={hasActiveFilters({
            categoryId,
            assignee,
            dueStatus,
            search: searchInput,
          })}
          onReset={resetFilters}
        />

        {isLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : isError ? (
          <div className="flex w-full items-center justify-center py-16">
            <span className="text-body font-body text-error-600">
              We couldn&apos;t load your tasks. Please try again.
            </span>
          </div>
        ) : items.length === 0 ? (
          <div className="flex w-full items-center justify-center py-16">
            <span className="text-body font-body text-subtext-color">
              {EMPTY_MESSAGE[tab]}
            </span>
          </div>
        ) : (
          <>
            <TableScrollContainer>
              <TaskManagerTable
                items={items}
                categories={categories}
                nurseryName={selectedNursery?.name ?? ""}
                isHistory={tab === "history"}
                activeColumn={effectiveSort.column}
                activeDir={effectiveSort.dir}
                onSort={handleSort}
                onView={(id) => navigate({ to: `/follow-up-actions/${id}` })}
                renderRowMenu={(action) => (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <IconButton
                        icon={<FeatherMoreVertical />}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {tab === "awaiting-acknowledgement" ? (
                        <>
                          <DropdownMenuItem
                            onClick={() =>
                              changeStatus(
                                action.id,
                                UpdateActionStatusDtoStatus.COMPLETED,
                                "Task signed off",
                              )
                            }
                          >
                            Sign off
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              changeStatus(
                                action.id,
                                UpdateActionStatusDtoStatus.OPEN,
                                "Task returned to open",
                              )
                            }
                          >
                            Return to open
                          </DropdownMenuItem>
                        </>
                      ) : tab === "history" ? (
                        <DropdownMenuItem
                          onClick={() =>
                            navigate({ to: `/follow-up-actions/${action.id}` })
                          }
                        >
                          View details
                        </DropdownMenuItem>
                      ) : (
                        <>
                          <DropdownMenuItem
                            onClick={() =>
                              changeStatus(
                                action.id,
                                UpdateActionStatusDtoStatus.COMPLETED,
                                "Task marked complete",
                              )
                            }
                          >
                            Mark complete
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              changeStatus(
                                action.id,
                                UpdateActionStatusDtoStatus.CANCELLED,
                                "Task cancelled",
                              )
                            }
                          >
                            Cancel task
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              />
            </TableScrollContainer>

            <Pagination
              page={page + 1}
              pageSize={pageSize}
              totalCount={total}
              onPageChange={(next) => setPage(next - 1)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              itemNoun={itemNounFor(tab)}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default TaskManagerPage;
